import React from 'react';
import { Activity, Heart, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';

export default function Services() {
  const services = [
    {
      icon: <Activity className="text-teal-600" size={48} />,
      title: 'Deep Tissue Massage',
      price: '£50',
      duration: '60 minutes',
      description: 'Therapeutic massage focusing on realigning deeper layers of muscles and connective tissue. Perfect for those with persistent muscle tension and chronic pain.',
      benefits: [
        'Relieves chronic muscle tension',
        'Improves posture',
        'Reduces inflammation',
        'Speeds up recovery from injuries'
      ]
    },
    {
      icon: <Heart className="text-teal-600" size={48} />,
      title: 'Swedish Massage',
      price: '£40',
      duration: '45 minutes',
      description: 'Gentle, flowing massage technique designed to relax the entire body and improve circulation. Perfect for stress relief and relaxation.',
      benefits: [
        'Promotes deep relaxation',
        'Improves blood circulation',
        'Eases muscle tension',
        'Enhances overall wellbeing'
      ]
    },
    {
      icon: <Droplets className="text-teal-600" size={48} />,
      title: 'Traditional Lymphatic Drainage Massage',
      price: '£40',
      duration: '60 to 75 minutes',
      description: 'Gentle, rhythmic massage technique using wave-like strokes that follow the path of the lymphatic system. Very light and slow; the skin is barely moved.',
      benefits: [
        'Therapeutic treatment for lymphoedema',
        'Reduces post-surgical swelling',
        'Helps with chronic inflammatory conditions',
        'Encourages lymph flow and detoxification'
      ]
    },
    {
      icon: <Droplets className="text-teal-600" size={48} />,
      title: 'Brazilian Lymphatic Drainage Massage',
      price: '£50',
      duration: '60 to 75 minutes',
      badge: 'Available from 1st December',
      description: 'Dynamic technique combining traditional lymphatic drainage principles with more vigorous, contouring techniques. Uses deeper pressure, fast-paced movements for both functional and aesthetic results.',
      benefits: [
        'Reduces bloating and fluid retention',
        'Sculpts the body and enhances curves',
        'Immediate visible changes',
        'Improves skin tone and reduces puffiness'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">My Services</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional massage therapy tailored to your needs. All treatments are 60 minutes and include consultation.
            </p>
            <p className="text-sm text-pink-600 font-semibold mt-4">
              Ladies Only (Male clients by referral)
            </p>
          </div>
        </div>

        {/* Services Detail Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center ${
                    service.badge ? 'bg-teal-50 rounded-2xl p-8' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 h-full flex items-center justify-center">
                      {service.icon}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                      {service.badge && (
                        <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {service.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="text-2xl font-bold text-teal-600">{service.price}</span>
                      <span className="text-gray-600">• {service.duration}</span>
                    </div>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    
                    <h3 className="font-bold text-gray-900 mb-3">Benefits:</h3>
                    <ul className="space-y-2 mb-6">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <span className="text-teal-600 mt-1">✓</span>
                          <span className="text-gray-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link 
                      to="/book" 
                      className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition"
                    >
                      Book This Service
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Lymphatic Drainage Comparison */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border-2 border-teal-200 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Understanding Lymphatic Drainage Techniques
              </h2>
              <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
                I offer both traditional and Brazilian lymphatic drainage techniques. Here's how they differ:
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-teal-50 rounded-xl p-6">
                  <h3 className="font-bold text-teal-900 mb-4 flex items-center text-xl">
                    <Droplets className="mr-2" size={24} />
                    Traditional Manual Lymphatic Drainage
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Technique:</strong> Gentle, rhythmic, wave-like strokes following lymphatic pathways</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Pressure:</strong> Very light and slow; skin barely moves</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Purpose:</strong> Primarily therapeutic - treats medical conditions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Effect:</strong> Encourages lymph flow and detoxification without visible aesthetic changes</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-teal-900 flex items-center text-xl">
                      <Droplets className="mr-2" size={24} />
                      Brazilian Lymphatic Drainage
                    </h3>
                    <span className="bg-teal-600 text-white text-xs font-semibold px-2 py-1 rounded">New!</span>
                  </div>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Technique:</strong> Vigorous contouring with deeper pressure and dynamic movements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Pressure:</strong> Medium to firm with fast-paced strokes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Purpose:</strong> Both functional and aesthetic - body sculpting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-teal-600 mr-2 font-bold">•</span>
                      <span><strong>Effect:</strong> Immediate visible changes - reduced puffiness, more defined contours, improved skin tone</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Treatment Location</h2>
            <p className="text-lg text-gray-600 mb-6">
              Treatments are provided at my comfortable home studio in Rugby. Mobile appointments 
              to your location are also available on request.
            </p>
            <p className="text-gray-600 mb-8">
              <strong>Home studio:</strong> Rugby, Warwickshire<br />
              <strong>Mobile service area:</strong> Rugby and surrounding areas
            </p>
            <p className="text-sm text-gray-500 mb-8">
              For mobile appointments, I bring everything needed including professional massage table, linens, and oils.
            </p>
            <Link 
              to="/book" 
              className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition text-lg"
            >
              Book Your Massage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
